import { UsersService } from "@/clients/services/UsersService";
import { QueryKeys } from "@/constants/query-keys";
import { useUser } from "@clerk/clerk-react";
import { useIsMutating, useQuery } from "@tanstack/react-query";

// TODO: remove this local type once `GET /api/v1/users` is typed with a proper
// Pydantic response model on the backend and the OpenAPI client is regenerated
// via `pnpm generate:api`. Currently the endpoint is typed as Dict[str, Any],
// so we preserve the original permissive shape and only surface the fields we
// rely on (e.g. `isOnboardingCompleted`) as typed members.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CurrentUser = Record<string, any> & {
  isOnboardingCompleted?: boolean;
};

export const useGetUser = () => {
  const { user: clerkUser, isLoaded } = useUser();
  const userId = clerkUser?.id;
  // Global route guards also mount on public screens. Wait for Clerk, and for
  // registration to create the backend record after activating its session.
  const isRegistering = useIsMutating({ mutationKey: [QueryKeys.REGISTER] }) > 0;
  const { data, isLoading, isError, error } = useQuery<CurrentUser>({
    queryKey: [QueryKeys.USER],
    enabled: isLoaded && Boolean(userId) && !isRegistering,
    queryFn: async () => {
      if (!userId) throw new Error("User profile requested without a Clerk user id");
      const { data } = await UsersService.getCurrentUserApiV1UsersGet(userId);
      const user = data as CurrentUser;
      // Backend returns the raw DynamoDB user where isOnboardingCompleted lives under
      // personalInfo. Lift it to the top level so consumers (PaywallGuard, no-plan-card)
      // and setQueryData writers (login/register/reset-password) agree on shape.
      const nestedFlag = user?.personalInfo?.isOnboardingCompleted;
      return {
        ...user,
        isOnboardingCompleted:
          typeof nestedFlag === "boolean" ? nestedFlag : user?.isOnboardingCompleted,
      };
    },
  });

  return { user: data, isLoading, isError, error };
};
